import styles from './ContactHost.module.scss';
import formStyles from 'legacy/common/components/form/Form.module.scss';

import classnames from 'classnames';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';

import { useContactHostMutation } from 'legacy/events/hooks';

import * as toast from 'legacy/common/utils/toast';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { useTranslation } from 'react-i18next';

const ContactHostSchema = Yup.object().shape({
  name: Yup.string().required('nameRequired'),
  email: Yup.string().email('emailInvalid').required('emailRequired'),
  message: Yup.string().required('messageRequired')
});

type ContactHostModalProps = {
  eventId: string;
  onClose: () => void;
};

const ContactHostModal: React.FC<ContactHostModalProps> = ({
  eventId,
  onClose
}) => {
  const { t } = useTranslation(['events']);
  const {
    mutate: contactHostMutate,
    isLoading,
    error,
    reset
  } = useContactHostMutation();

  return (
    <Modal
      type={ModalType.SMALL}
      visible
      onClose={() => {
        onClose();
        reset();
      }}
    >
      <div className={styles.container}>
        <Formik
          validationSchema={ContactHostSchema}
          initialValues={{
            name: '',
            email: '',
            message: ''
          }}
          onSubmit={(values) => {
            contactHostMutate(
              { eventId, details: values },
              {
                onSuccess: () => {
                  onClose();
                  toast.success(t('events:contactHost.success'));
                }
              }
            );
          }}
        >
          {({ errors, touched }) => (
            <Form className={formStyles.container}>
              <h1 className={styles.title}>{t('events:contactHost.title')}</h1>
              <div className={formStyles.formError}>{error?.message}</div>
              <div className={formStyles.group}>
                <div className={formStyles.label}>
                  {t('events:contactHost.nameLabel')}
                </div>
                <Field
                  id="name"
                  name="name"
                  type="text"
                  placeholder={t('events:contactHost.namePlaceholder')}
                  className={classnames(formStyles.input, {
                    [formStyles.errorField]: errors.name && touched.name
                  })}
                />
                {errors.name && touched.name && (
                  <div className={formStyles.error}>
                    {t(`events:contactHost.${errors.name}`)}
                  </div>
                )}
              </div>

              <div className={formStyles.group}>
                <div className={formStyles.label}>
                  {t('events:contactHost.emailLabel')}
                </div>
                <Field
                  id="email"
                  name="email"
                  type="text"
                  placeholder={t('events:contactHost.emailPlaceholder')}
                  className={classnames(formStyles.input, {
                    [formStyles.errorField]: errors.email && touched.email
                  })}
                />
                {errors.email && touched.email && (
                  <div className={formStyles.error}>
                    {t(`events:contactHost.${errors.email}`)}
                  </div>
                )}
              </div>

              <div className={formStyles.group}>
                <div className={formStyles.label}>
                  {t('events:contactHost.messageLabel')}
                </div>
                <Field
                  rows={9}
                  id="message"
                  name="message"
                  as="textarea"
                  placeholder={t('events:contactHost.messagePlaceholder')}
                  className={classnames(formStyles.textarea, {
                    [formStyles.errorField]: errors.message && touched.message
                  })}
                />
                {errors.message && touched.message && (
                  <div className={formStyles.error}>
                    {t(`events:contactHost.${errors.message}`)}
                  </div>
                )}
              </div>

              <div className={formStyles.group}>
                <Button isSubmit loading={isLoading} type={ButtonType.Primary}>
                  {t('events:contactHost.sendMessage')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default ContactHostModal;
